<template>
  <Modal v-model="showModal" size="7xl">
    <template #title>
      <template v-if="hasValidExpenseData">
        Expense #{{ expense?.id }} - Preview
      </template>
      <template v-else>
        Expense Preview
      </template>
    </template>
    <template #content>
      <div>
        <!-- Loading State -->
        <div v-if="loading" class="flex items-center justify-center p-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span class="ml-3 text-gray-600 dark:text-dark-400">Loading expense details...</span>
        </div>
        
        <!-- Error State -->
        <div v-else-if="error" class="p-8 text-center">
          <div class="text-red-600 dark:text-red-400">
            <i class="fas fa-exclamation-triangle text-2xl mb-2"></i>
            <p>{{ error }}</p>
          </div>
          <button 
            @click="fetchExpenseDetails" 
            class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
        
        <!-- Expense Content -->
        <div v-else-if="hasValidExpenseData" id="printable-expense" class="bg-white dark:bg-dark-800 mx-auto p-4" style="width: 9.5in; height: 5.5in;">
        <!-- Header -->
        <div class="relative mb-3">
          <div>
            <div class="text-xl font-bold mb-1" style="letter-spacing: 0.025em;">IGW Digital Printing Service</div>
            <div class="text-sm">27 A Dacudao Ave, Poblacion District</div>
            <div class="text-sm">Davao City, Philippines</div>
            <div class="text-sm">Tel: 0927-426-2634</div>
          </div>
          <div class="absolute top-0 right-0 flex items-start gap-4">
            <!-- Receipt Image -->
            <div v-if="expense?.receipt_image_url" class="text-center">
              <div class="relative cursor-pointer" @click="openImagePreview(getReceiptImageUrl(expense.receipt_image_url))">
                <img 
                  :src="getReceiptImageUrl(expense.receipt_image_url)" 
                  alt="Receipt" 
                  style="width: 80px; height: 80px; object-fit: cover;"
                  class="rounded-lg shadow-sm"
                />
                <button 
                  class="absolute bottom-1 right-1 bg-white dark:bg-dark-700 bg-opacity-90 p-1 rounded-full shadow-md hover:bg-opacity-100 transition-all"
                  style="font-size: 0.6rem;"
                  title="View full image"
                >
                  <i class="fas fa-expand text-gray-600 dark:text-gray-300"></i>
                </button>
              </div>
              <p class="text-xs text-gray-500 dark:text-dark-400 text-center">Receipt</p>
            </div>
            <!-- Payment Proof Image -->
            <div v-if="expense?.payment_proof_url && expense?.payment_method !== 'cash'" class="text-center">
              <div class="relative cursor-pointer" @click="openImagePreview(getReceiptImageUrl(expense.payment_proof_url))">
                <img 
                  :src="getReceiptImageUrl(expense.payment_proof_url)" 
                  alt="Payment Proof" 
                  style="width: 80px; height: 80px; object-fit: cover;"
                  class="rounded-lg shadow-sm"
                />
                <button 
                  class="absolute bottom-1 right-1 bg-white dark:bg-dark-700 bg-opacity-90 p-1 rounded-full shadow-md hover:bg-opacity-100 transition-all"
                  style="font-size: 0.6rem;"
                  title="View full image"
                >
                  <i class="fas fa-expand text-gray-600 dark:text-gray-300"></i>
                </button>
              </div>
              <p class="text-xs text-gray-500 dark:text-dark-400 text-center">Payment Proof</p>
            </div>
          </div>
        </div>

        <!-- Expense Information -->
        <div class="grid grid-cols-2 gap-x-8 mb-3">
          <div>
            <h3 class="text-sm font-semibold mb-0.5">Expense Information</h3>
            <div class="grid gap-y-0">
              <div class="flex text-xs">
                <span class="text-gray-600 w-24">Expense ID:</span>
                <span>{{ expense?.id }}</span>
              </div>
              <div class="flex text-xs">
                <span class="text-gray-600 w-24">Category:</span>
                <span>{{ expense?.group_name || 'Uncategorized' }}</span>
              </div>
              <div class="flex text-xs">
                <span class="text-gray-600 w-24">Department:</span>
                <span>{{ expense?.department_name || 'Not Assigned' }}</span>
              </div>
              <div class="flex text-xs">
                <span class="text-gray-600 w-24">Supplier:</span>
                <span>{{ expense?.supplier_name }}</span>
              </div>
            </div>
          </div>
          <div>
            <h3 class="text-sm font-semibold mb-0.5">Additional Details</h3>
            <div class="grid gap-y-0">
              <div class="flex text-xs">
                <span class="text-gray-600 w-24">Date:</span>
                <span>{{ formatDate(expense?.expense_date) }}</span>
              </div>
              <div class="flex text-xs">
                <span class="text-gray-600 w-24">Status:</span>
                <span>{{ formatStatus(expense?.status) }}</span>
              </div>
              <div class="flex text-xs">
                <span class="text-gray-600 w-24">Created By:</span>
                <span>{{ expense?.created_by_name }}</span>
              </div>
              <div class="flex text-xs">
                <span class="text-gray-600 w-24">Payment:</span>
                <span>{{ formatPaymentMethod(expense?.payment_method) }}</span>
              </div>
              <div v-if="expense?.payment_method === 'online'" class="flex text-xs">
                <span class="text-gray-600 w-24">Platform:</span>
                <span>{{ expense?.online_platform || 'Not specified' }}</span>
              </div>
              <div v-if="expense?.payment_method === 'online'" class="flex text-xs">
                <span class="text-gray-600 w-24">Reference:</span>
                <span>{{ expense?.transaction_reference || 'Not specified' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Expense Items -->
        <div class="mb-3">
          <h3 class="text-sm font-semibold mb-0.5">Expense Items</h3>
          <table class="w-full text-xs">
            <thead>
              <tr class="border-b border-gray-200">
                <th class="text-left py-0.5 px-2">Item</th>
                <th class="text-center py-0.5 px-2">Quantity</th>
                <th class="text-right py-0.5 px-2">Unit Price</th>
                <th class="text-right py-0.5 px-2">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in expense?.items" :key="item.id" class="border-b border-gray-200">
                <td class="py-0.5 px-2">{{ item.product_name }}</td>
                <td class="text-center py-0.5 px-2">{{ item.quantity }}</td>
                <td class="text-right py-0.5 px-2">₱{{ formatPrice(item.unit_price) }}</td>
                <td class="text-right py-0.5 px-2">₱{{ formatPrice(item.total_cost) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="3" class="text-right py-0.5 px-2 font-semibold">Total Amount:</td>
                <td class="text-right py-0.5 px-2 font-semibold">₱{{ formatPrice(calculateTotal()) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Notes -->
        <div v-if="expense?.notes">
          <h3 class="text-sm font-semibold mb-0.5">Notes</h3>
          <p class="text-xs">{{ expense?.notes }}</p>
        </div>
      </div>
      <div v-else class="p-6 text-center">
        <p class="text-gray-500 dark:text-dark-400">No expense data available for preview</p>
      </div>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-end gap-3 px-6 py-3">
        <button
          @click="$emit('update:modelValue', false)"
          class="px-4 py-2 text-gray-700 dark:text-dark-200 border border-gray-300 dark:border-dark-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
        >
          Close
        </button>
        <button
          v-if="hasValidExpenseData"
          @click="downloadPDF"
          class="px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600 flex items-center gap-2"
        >
          <i class="fas fa-download"></i>
          Download PDF
        </button>
        <button
          v-if="hasValidExpenseData"
          @click="printExpense"
          class="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center gap-2"
        >
          <i class="fas fa-print"></i>
          Print Expense
        </button>
        
        <!-- Approval buttons (only shown when opened from approval context) -->
        <template v-if="isApprovalContext && hasValidExpenseData && hasPermission('expenses.approve')">
          <button
            @click="handleApprove"
            :disabled="isProcessingApproval"
            class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            <i v-if="isProcessingApproval" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-check"></i>
            {{ isProcessingApproval ? 'Approving...' : 'Approve' }}
          </button>
          <button
            @click="handleReject"
            :disabled="isProcessingApproval"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            <i v-if="isProcessingApproval" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-times"></i>
            {{ isProcessingApproval ? 'Processing...' : 'Reject' }}
          </button>
        </template>
      </div>
    </template>
  </Modal>

  <!-- Image Preview Modal -->
  <Modal v-model="showImagePreview" size="xl">
    <template #title>Image Preview</template>
    <template #content>
      <div class="relative p-6">
        <img 
          :src="selectedImage" 
          alt="Image Preview"
          class="w-full h-auto rounded-lg"
        />
      </div>
    </template>
    <template #footer>
      <div class="flex justify-end gap-3">
        <button
          @click="showImagePreview = false"
          class="px-4 py-2 text-gray-700 dark:text-dark-200 border border-gray-300 dark:border-dark-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
        >
          Close
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import Modal from '@/components/common/Modal.vue';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { getImageUrl } from '@/utils/imageUtils';
import { useApi } from '@/composables/useApi';
import { useAuth } from '@/composables/useAuth';
import { useToast } from '@/composables/useToast';

const props = defineProps({
  modelValue: {
    type: Boolean,  
    required: true
  },
  expense: {
    type: Object,
    required: true,
    default: () => ({})  // Provide a default empty object
  },
  isApprovalContext: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'approved', 'rejected']);

// Initialize composables
const api = useApi();
const { hasPermission } = useAuth();
const toast = useToast();

const showModal = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// Image preview state
const showImagePreview = ref(false);
const selectedImage = ref(null);

// Approval state
const isProcessingApproval = ref(false);

// Data loading state
const loading = ref(false);
const error = ref(null);
const expenseData = ref(null);

// Computed property for the expense data (either from props or fetched data)
const expense = computed(() => expenseData.value || props.expense);

// Function to open image preview modal
const openImagePreview = (imageUrl) => {
  selectedImage.value = imageUrl;
  showImagePreview.value = true;
  //console.log('Opening image preview:', imageUrl);
};

// Add a computed property to check if we have valid expense data
const hasValidExpenseData = computed(() => {
  return expense.value && expense.value.id;
});

// Check if we need to fetch full expense data (if items array is missing)
const needsFullData = computed(() => {
  return props.expense && props.expense.id && !props.expense.items;
});

// Function to fetch full expense details
const fetchExpenseDetails = async () => {
  const expenseId = props.expense.id;
  if (!expenseId) {
    error.value = 'No expense ID provided';
    return;
  }

  try {
    loading.value = true;
    error.value = null;
    
    console.log('Fetching full expense details for ID:', expenseId);
    const response = await api.get('/expenses', {
      params: {
        id: expenseId
      }
    });
    
    if (response.data?.data && response.data.data.length > 0) {
      expenseData.value = response.data.data[0];
      console.log('Expense details loaded:', response.data.data[0]);
    } else {
      throw new Error('Expense details not found');
    }
  } catch (err) {
    console.error('Error loading expense details:', err);
    error.value = err.response?.data?.message || 'Failed to load expense details';
  } finally {
    loading.value = false;
  }
};

// Fetch full data when component mounts if needed
onMounted(() => {
  if (needsFullData.value) {
    console.log('ExpensePreview: Need to fetch full data for expense:', props.expense.id);
    fetchExpenseDetails();
  }
});

// Format functions
const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-PH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatPrice = (price) => {
  return Number(price).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatStatus = (status) => {
  if (!status) return '';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const calculateTotal = () => {
  if (!expense.value?.items) return 0;
  return expense.value.items.reduce((sum, item) => sum + (item.total_cost || 0), 0);
};

const getReceiptImageUrl = (imageUrl) => {
  return getImageUrl(imageUrl);
};

const formatPaymentMethod = (method) => {
  if (!method) return 'Not specified';
  return method === 'cash' ? 'Cash' : 'Online Payment';
};

// Approval handlers
const handleApprove = async () => {
  if (!props.expense || isProcessingApproval.value) return;
  
  try {
    isProcessingApproval.value = true;
    
    const response = await api.post(`/expenses/${props.expense.id}/approve`, {
      status: 'approved',
      approved_by: 'current_user' // This should be handled by the backend
    });
    
    if (response.data) {
      toast.success('Expense approved successfully');
      
      // Emit event to parent component to refresh the approvals list
      emit('approved', props.expense);
    }
  } catch (error) {
    console.error('Error approving expense:', error);
    toast.error(error.response?.data?.message || 'Failed to approve expense');
  } finally {
    isProcessingApproval.value = false;
  }
};

const handleReject = async () => {
  if (!props.expense || isProcessingApproval.value) return;
  
  const reason = prompt('Please provide a reason for rejecting this expense:');
  if (!reason || reason.trim() === '') {
    toast.warning('Please provide a reason for rejection');
    return;
  }
  
  try {
    isProcessingApproval.value = true;
    
    const response = await api.post(`/expenses/${props.expense.id}/reject`, {
      status: 'rejected',
      rejection_reason: reason.trim(),
      rejected_by: 'current_user' // This should be handled by the backend
    });
    
    if (response.data) {
      toast.success('Expense rejected successfully');
      
      // Emit event to parent component to refresh the approvals list
      emit('rejected', props.expense);
    }
  } catch (error) {
    console.error('Error rejecting expense:', error);
    toast.error(error.response?.data?.message || 'Failed to reject expense');
  } finally {
    isProcessingApproval.value = false;
  }
};

// Print function
const printExpense = async () => {
  try {
    const element = document.getElementById('printable-expense');
    
    // Add extra padding at the bottom to ensure all content fits
    element.style.paddingBottom = '20px';
    
    const canvas = await html2canvas(element, {
      scale: 4,
      logging: false,
      width: 906, // 9.5in * 96dpi
      windowWidth: 906,
      useCORS: true,
      backgroundColor: '#ffffff',
      imageTimeout: 0,
      letterRendering: true,
      onclone: (clonedDoc) => {
        const style = clonedDoc.createElement('style');
        style.innerHTML = `
          @font-face {
            font-family: 'Arial';
            src: local('Arial');
          }
          #printable-expense {
            font-family: 'Arial', sans-serif !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            padding-bottom: 20px !important;
          }
          #printable-expense .text-xl {
            letter-spacing: 0.025em !important;
            word-spacing: 0.05em !important;
          }
        `;
        clonedDoc.head.appendChild(style);
      }
    });
    
    // Reset the padding after canvas generation
    element.style.paddingBottom = '';
    
    // Create a temporary PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'in',
      format: 'letter',
      compress: true,
      hotfixes: ['px_scaling']
    });

    pdf.addImage(
      canvas.toDataURL('image/jpeg', 1.0),
      'JPEG',
      0,
      0,
      8.5, // width
      5.5, // height
      undefined,
      'FAST'
    );

    // Convert PDF to blob and create URL
    const blob = pdf.output('blob');
    const blobUrl = URL.createObjectURL(blob);

    // Create a temporary iframe for printing
    const printFrame = document.createElement('iframe');
    printFrame.style.display = 'none';
    printFrame.src = blobUrl;

    printFrame.onload = () => {
      try {
        // Listen for the afterprint event
        const handlePrintComplete = () => {
          window.removeEventListener('afterprint', handlePrintComplete);
          // Clean up after printing is complete
          document.body.removeChild(printFrame);
          URL.revokeObjectURL(blobUrl);
        };

        window.addEventListener('afterprint', handlePrintComplete);
        
        const printWindow = printFrame.contentWindow;
        printWindow.focus(); // Focus the print window
        printWindow.print();
      } catch (error) {
        console.error('Print error:', error);
        // Clean up if there's an error
        document.body.removeChild(printFrame);
        URL.revokeObjectURL(blobUrl);
      }
    };

    document.body.appendChild(printFrame);
  } catch (error) {
    console.error('Error preparing print:', error);
  }
};

// Download PDF function
const downloadPDF = async () => {
  try {
    const element = document.getElementById('printable-expense');
    
    // Add extra padding at the bottom to ensure all content fits
    element.style.paddingBottom = '20px';
    
    const canvas = await html2canvas(element, {
      scale: 4,
      logging: false,
      width: 906, // 9.5in * 96dpi
      windowWidth: 906,
      useCORS: true,
      backgroundColor: '#ffffff',
      imageTimeout: 0,
      letterRendering: true,
      onclone: (clonedDoc) => {
        const style = clonedDoc.createElement('style');
        style.innerHTML = `
          @font-face {
            font-family: 'Arial';
            src: local('Arial');
          }
          #printable-expense {
            font-family: 'Arial', sans-serif !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            padding-bottom: 20px !important;
          }
          #printable-expense .text-xl {
            letter-spacing: 0.025em !important;
            word-spacing: 0.05em !important;
          }
        `;
        clonedDoc.head.appendChild(style);
      }
    });
    
    // Reset the padding after canvas generation
    element.style.paddingBottom = '';
    
    // Calculate dimensions
    const imgWidth = 8.5;
    const pageHeight = 5.5;
    
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'in',
      format: 'letter',
      compress: true,
      hotfixes: ['px_scaling']
    });

    pdf.addImage(
      canvas.toDataURL('image/jpeg', 1.0),
      'JPEG',
      0,
      0,
      imgWidth,
      pageHeight,
      undefined,
      'FAST'
    );

    pdf.setProperties({
      title: `Expense #${props.expense?.id || 'unknown'}`,
      subject: 'Expense Details',
      creator: 'IGW Digital Printing Service',
      author: 'IGW Digital Printing Service'
    });

    pdf.save(`expense-${props.expense?.id || 'unknown'}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
  }
};
</script>

<style scoped>
/* Preview mode styles */
.preview-mode {
  width: 8.5in;
  min-height: 5.5in;
  margin: 0 auto;
  padding: 1rem;
  background: white;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  font-family: Arial, sans-serif;
  font-size: 12px;
  line-height: 1.4;
}

/* Print mode styles */
@media print {
  @page {
    size: letter portrait;
    margin: 0;
  }

  body {
    margin: 0;
    padding: 0;
    width: 8.5in;
    height: 5.5in;
  }

  #printable-expense {
    width: 8.5in !important;
    min-height: 5.5in !important;
    margin: 0 !important;
    padding: 0.5in !important;
    overflow: hidden !important;
    box-shadow: none !important;
    font-size: 12px !important;
    background-color: white !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Hide modal elements during print */
  .modal-header,
  .modal-footer,
  .print\:hidden {
    display: none !important;
  }

  /* Ensure content is visible */
  #printable-expense,
  #printable-expense * {
    visibility: visible !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style> 